(() => {
  class DioHomeEffects {
    constructor() {
      this.stickyObservers = new Map();
      this.footerReveal = null;
      this.footerRevealFrame = 0;
      this.onSectionLoad = (event) => {
        this.init(event.target);
        this.initFooterReveal();
        this.scheduleFooterRevealMeasure();
      };
      this.onSectionUnload = (event) => {
        this.destroyWithin(event.target);
        this.scheduleFooterRevealMeasure();
      };
      this.onPageHide = (event) => {
        if (!event.persisted) this.destroy();
      };
      this.onPageShow = () => this.scheduleFooterRevealMeasure();

      this.init(document);
      this.initFooterReveal();

      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
    }

    init(root) {
      root.querySelectorAll?.('[data-dio-sticky-cta]').forEach((sticky) => this.initSticky(sticky));
    }

    initSticky(sticky) {
      if (sticky.dataset.dioReady === 'true' || window.Shopify?.designMode) return;
      const hero = document.querySelector('[data-dio-hero]');
      const footer = document.querySelector('.footer');
      if (!hero || !('IntersectionObserver' in window)) return;

      sticky.dataset.dioReady = 'true';
      const revealSpacer = document.querySelector('[data-homepage-footer-reveal-spacer]');
      const state = {
        heroVisible: true,
        footerVisible: false,
        revealSpacerVisible: false,
        observers: [],
        update: null,
      };
      state.update = () => {
        const footerVisible = document.body.classList.contains('homepage-footer-reveal-enabled')
          ? state.revealSpacerVisible
          : state.footerVisible;
        sticky.classList.toggle('is-visible', !state.heroVisible && !footerVisible);
      };
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          state.heroVisible = entry.isIntersecting && entry.intersectionRatio > 0.18;
          state.update();
        },
        { threshold: [0, 0.18] }
      );
      heroObserver.observe(hero);
      state.observers.push(heroObserver);

      if (footer) {
        const footerObserver = new IntersectionObserver(([entry]) => {
          state.footerVisible = entry.isIntersecting;
          state.update();
        });
        footerObserver.observe(footer);
        state.observers.push(footerObserver);
      }

      if (revealSpacer) {
        const revealObserver = new IntersectionObserver(([entry]) => {
          state.revealSpacerVisible = entry.isIntersecting;
          state.update();
        });
        revealObserver.observe(revealSpacer);
        state.observers.push(revealObserver);
      }

      this.stickyObservers.set(sticky, state);
    }

    initFooterReveal() {
      if (this.footerReveal || window.Shopify?.designMode) return;

      const element = document.querySelector('[data-homepage-footer-reveal]');
      const spacer = document.querySelector('[data-homepage-footer-reveal-spacer]');
      if (!element || !spacer) return;

      const state = {
        element,
        observer: null,
        onResize: () => this.scheduleFooterRevealMeasure(),
      };

      if ('ResizeObserver' in window) {
        state.observer = new ResizeObserver(state.onResize);
        state.observer.observe(element);
      }

      window.addEventListener('resize', state.onResize, { passive: true });
      window.visualViewport?.addEventListener('resize', state.onResize, { passive: true });
      this.footerReveal = state;
      this.scheduleFooterRevealMeasure();

      document.fonts?.ready.then(() => {
        if (this.footerReveal === state) this.scheduleFooterRevealMeasure();
      });
    }

    scheduleFooterRevealMeasure() {
      if (!this.footerReveal || this.footerRevealFrame) return;
      this.footerRevealFrame = window.requestAnimationFrame(() => {
        this.footerRevealFrame = 0;
        this.measureFooterReveal();
      });
    }

    measureFooterReveal() {
      const state = this.footerReveal;
      if (!state?.element.isConnected) return;

      const footerHeight = Math.ceil(state.element.getBoundingClientRect().height);
      const viewportCandidates = [document.documentElement.clientHeight, window.innerHeight];
      if (window.visualViewport?.height) viewportCandidates.push(window.visualViewport.height);
      const viewportHeight = Math.floor(Math.min(...viewportCandidates.filter((height) => height > 0)));
      const canReveal = footerHeight > 0 && footerHeight <= viewportHeight;

      document.body.style.setProperty('--homepage-footer-reveal-height', `${footerHeight}px`);
      document.body.classList.toggle('homepage-footer-reveal-enabled', canReveal);
      state.element.dataset.revealMode = canReveal ? 'fixed' : 'flow';
      this.refreshStickyVisibility();
    }

    refreshStickyVisibility() {
      this.stickyObservers.forEach((state) => state.update?.());
    }

    destroyFooterReveal() {
      if (!this.footerReveal) return;
      const { element, observer, onResize } = this.footerReveal;
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.cancelAnimationFrame(this.footerRevealFrame);
      this.footerRevealFrame = 0;
      element.removeAttribute('data-reveal-mode');
      document.body.classList.remove('homepage-footer-reveal-enabled');
      document.body.style.removeProperty('--homepage-footer-reveal-height');
      this.footerReveal = null;
    }

    destroyWithin(root) {
      root.querySelectorAll?.('[data-dio-sticky-cta]').forEach((sticky) => {
        this.stickyObservers.get(sticky)?.observers.forEach((observer) => observer.disconnect());
        this.stickyObservers.delete(sticky);
      });
    }

    destroy() {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      this.stickyObservers.forEach((state) => state.observers.forEach((observer) => observer.disconnect()));
      this.stickyObservers.clear();
      this.destroyFooterReveal();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new DioHomeEffects(), { once: true });
  } else {
    new DioHomeEffects();
  }
})();
